import GraphRect from "./graph_rect";
import GraphContainer, { getOrSet } from "./graph_container";
import DataPoint from "./graph_data/data_point";
import GraphGroup from "./graph_group";
import DataGraph from "./graph_data/data_graph";
import DataBase from "./graph_data/data_base";
import DataArbitrary from "./graph_data/data_arbitrary";
import getRelativeCoordinates from "../../utils/get-relative-coordinates";

class GraphChart extends GraphRect {
    constructor(xax, yax, xrange, yrange) {
        super();

        this._parent = new GraphGroup()
            .setAttribute("name", "chart-objects")
            .setAttribute("clip-path", `url(#clip_${this._uuid})`);

        this.append(this._parent);

        this._axes = Object.freeze({ x: xax, y: yax });
        this._ranges = Object.freeze({ x: xrange, y: yrange });
        this._step = 1;
        this._stepMinor = 0.25;

        this._activeDatapoint = new DataPoint(null).radius(5).color("red");
        /**
         * @type {DataBase[]}
         */
        this._data = [];

        this._editingPoints = false;

        this._hoverLabel = GraphContainer
            .create("text")
            .setAttribute("name", "hover-label")
            .setAttribute("font-size", "16px")
            .setAttribute("text-anchor", "middle")
            .setAttribute("pointer-events", "none")
            .innerText("(0, 0)");

        if (!Q3.editMode) this.addEventListener("mousemove", this._onMouseOver.bind(this));
    }

    fromCanvasToNumbers(vec) {
        return vec
            .setX(THREE.Math.mapLinear(vec.x / this.width(), 0, 1, this._ranges.x.x, this._ranges.x.y))
            .setY(-THREE.Math.mapLinear(vec.y / this.height(), 0, 1, this._ranges.y.x, this._ranges.y.y));
    }

    _onMouseOver(event) {
        const target = event.target;

        if (this._rect.contains(target) || this._activeDatapoint.contains(target)) {
            this._hoverLabel.setAttribute("display", "none");
        } else {
            const dp = this._data.find(dp => dp.contains(target.parentElement));

            if (!dp) this._hoverLabel.setAttribute("display", "none");
            else {
                const vec = new THREE.Vector2();
                const { layerX: x, layerY: y } = event;
                this.fromCanvasToNumbers(getRelativeCoordinates(event, undefined, vec).sub(this.origin()));

                switch (dp.type) {
                    case "graph": vec.setY(dp.func()(Object.assign({ x: vec.x }, dp.params()))); break;
                    case "point": vec.copy(dp.position()); break;
                }

                const toString = val => {
                    if (1e-2 <= Math.abs(val) && Math.abs(val) <= 1e3 || Math.abs(val) < 1e-10) return val % 1 === 0
                        ? val
                        : parseFloat(val.toFixed(2));
                    return val.toExponential(2);
                };

                this._hoverLabel
                    .removeAttribute("display")
                    .setAttribute("x", x)
                    .setAttribute("y", y)
                    .innerText(`(${toString(vec.x)}, ${toString(vec.y)})`);
            }
        }
    }

    /**
     * @returns {this}
     */
    editingPoints() { return getOrSet.call(this, this, "_editingPoints", ...arguments); }

    /**
     * @returns {this}
     */
    stepMinor() { return getOrSet.call(this, this, "_stepMinor", ...arguments); }

    /**
     * @returns {this}
     */
    step() { return getOrSet.call(this, this, "_step", ...arguments); }

    get activeDatapoint() { return this._activeDatapoint; }

    /**
     * @returns {this}
     */
    data() { return getOrSet.call(this, this, "_data", ...arguments); }

    redraw() {
        super.redraw();

        const xrange = this._ranges.x, yrange = this._ranges.y;
        const xfun = this._axes.x.value, yfun = this._axes.y.value;
        this._parent.erase();

        /**
         * 
         * @param {DataPoint} datapoint 
         */
        const createPoint = datapoint => {
            const pos = datapoint.position();
            const x = this._origin.x + THREE.Math.mapLinear(xfun(pos.x), xrange.x, xrange.y, 0, this._size.width);
            const y = this._origin.y + THREE.Math.mapLinear(-yfun(pos.y), yrange.x, yrange.y, 0, this._size.height);

            datapoint
                .origin(x, y)
                .redraw();

            this._parent.append(datapoint);
        };

        /**
         * 
         * @param {DataArbitrary} datapoint 
         */
        const createArbitrary = datapoint => {
            const points = datapoint.points();
            const pts = [];

            const addPoint = (x, y) => {
                const _x = this._origin.x + THREE.Math.mapLinear(xfun(x), xrange.x, xrange.y, 0, this._size.width);
                const _y = this._origin.y + THREE.Math.mapLinear(-yfun(y), yrange.x, yrange.y, 0, this._size.height);

                pts.push(_x, _y);
            }

            points.forEach(({ x, y }) => addPoint(x, y));

            if (pts.length > 1) {
                datapoint
                    .drawpoints(pts)
                    .redraw();

                this._parent.append(datapoint);
            }
        };

        /**
         * 
         * @param {DataGraph} datapoint 
         */
        const createGraph = datapoint => {
            const func = datapoint.func();
            const range = datapoint.range();
            const customDomain = datapoint.customDomain();
            const [rmin, rmax] = customDomain ? range.toArray() : [-Infinity, Infinity];
            const xmin = THREE.Math.clamp(rmin, xrange.x, xrange.y);
            const xmax = THREE.Math.clamp(rmax, xrange.x, xrange.y);
            const pts = [];

            const addPoint = (x, y) => {
                const _x = this._origin.x + THREE.Math.mapLinear(xfun(x), xrange.x, xrange.y, 0, this._size.width);
                const _y = this._origin.y + THREE.Math.mapLinear(-yfun(y), yrange.x, yrange.y, 0, this._size.height);

                pts.push(_x, _y);
            };

            const assignPars = x => Object.assign({ x }, datapoint.params());

            for (let x = xmin; x <= xmax; x += this._stepMinor * 0.1) {
                const y = func.call(datapoint, assignPars(x));

                addPoint(x, y);
            }

            addPoint(xmax, func.call(datapoint, assignPars(xmax)));

            if (pts.length > 1) {
                const path = `M${pts[0]} ${pts[1]} L${pts.slice(2).join(" ")}`;

                datapoint
                    .path(path)
                    .redraw();

                this._parent.append(datapoint);
            }
        };

        this._data.forEach(datapoint => {
            if (!datapoint.visibility()) return;

            switch (datapoint.type) {
                case "point":
                    createPoint(datapoint);
                    break;
                case "graph":
                    createGraph(datapoint);
                    break;
                case "arbitrary":
                    createArbitrary(datapoint);
                    break;
            }
        });

        if (this._editingPoints) {
            const activePos = this._activeDatapoint.position();
            const activeX = this._origin.x + THREE.Math.mapLinear(xfun(activePos.x), xrange.x, xrange.y, 0, this._size.width);
            const activeY = this._origin.y + THREE.Math.mapLinear(-yfun(activePos.y), yrange.x, yrange.y, 0, this._size.height);

            this._activeDatapoint
                .origin(activeX, activeY)
                .redraw();

            this._parent.append(this._activeDatapoint);
        }

        this._parent.append(this._hoverLabel);

        return this;
    }
}

export default GraphChart;
export { GraphChart };